import React, { useState, useEffect, useRef, ReactNode, CSSProperties } from "react";

interface CollapsibleProps {
    duration?: number;
    easing?: string;
    isOpen: boolean;
    children: ReactNode;
}

const useCollapsiblePresenter = (isOpen: boolean, duration: number, easing: string) => {
    // ** State ** //
    const [height, setHeight] = useState<string>(isOpen ? "auto" : "0");
    const [opacity, setOpacity] = useState<number>(isOpen ? 1 : 0);
    const contentRef = useRef<HTMLDivElement>(null);

    // ** Styles ** //

    const collapsibleStyle: CSSProperties = {
        height,
        opacity,
        overflow: "hidden",
        transition: `height ${duration}ms ${easing}, opacity ${duration}ms ${easing}`,
    };

    // ** Effects ** //

    useEffect(() => {
        if (isOpen) {
            setOpacity(1);
            if (contentRef.current) {
                setHeight(`${contentRef.current.scrollHeight}px`);
            }
            const timer = setTimeout(() => setHeight("auto"), duration);
            return () => clearTimeout(timer);
        } else {
            if (contentRef.current) {
                setHeight(`${contentRef.current.scrollHeight}px`);
            }
            const timer = setTimeout(() => {
                setOpacity(0);
                setHeight("0");
            }, 10);
            return () => clearTimeout(timer);
        }
    }, [isOpen, duration]);

    return {
        uiModel: {
            collapsibleStyle,
            contentRef,
            height,
            opacity,
        },
    };
};

const Collapsible: React.FC<CollapsibleProps> = ({ children, duration = 500, easing = "ease-in-out", isOpen }) => {
    const { uiModel } = useCollapsiblePresenter(isOpen, duration, easing);

    return (
        <div ref={uiModel.contentRef} style={uiModel.collapsibleStyle}>
            {children}
        </div>
    );
};

export default Collapsible;
